import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  TablePagination,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  ListSubheader,
  Checkbox,
  FormControlLabel,
  Switch,
  ListItemText,
  Autocomplete,
  Snackbar,
  Backdrop,
  LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Add, Edit, Delete, Visibility, Close, VisibilityOff, CloudUpload, AttachFile, Image, Search, CalendarMonth, LibraryBooks } from '@mui/icons-material';
import ApiService from '../../../services/api';
import SchedulePostModal from '../../Dashboard/components/SchedulePostModal';
import TaskCatalogModal from '../components/TaskCatalogModal';
import SORTED_GMB_CATEGORIES from '../../../constants/gmbCategories';
import GMB_ATTRIBUTES, { ALL_GMB_ATTRIBUTES } from '../../../constants/gmbAttributes';
import SORTED_GMB_SERVICES from '../../../constants/gmbServices';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#f5f7fa',
  minHeight: 'calc(100vh - 200px)'
}));

const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px'
}));

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  borderRadius: '12px'
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  borderRadius: '12px'
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: '#f8f9fa',
  color: '#2c3e50'
}));

// Predefined Task Titles with Descriptions
const PREDEFINED_TASKS = {
  'Add missing phone number': 'Add your phone number to receive more client calls instantly.',
  'Add Chat option': 'Enable chat to connect with customers in real time.',
  'Correct business address': 'Ensure your address is accurate so customers can easily find you.',
  'Update Business Location': 'Update your new location to guide clients right to your door.',
  'Add/update business website': 'Link your website to drive traffic and boost online visibility.',
  'Video Verification of your GBP': 'Verify your profile through video to make your business more trustworthy.',
  'Verify Suspended GBP': 'Reactivate your suspended profile and get back in front of customers.',
  'Fix duplicate GBP profile': 'Merge or remove duplicates to avoid confusion and improve ranking.',
  'Add missing opening hours': 'Add your working hours so clients know when to visit.',
  'Add Payments option': 'Show accepted payment methods to offer customers easy convenience.',
  'Update holiday hours or Special hours': 'Keep customers informed about your holiday timings.',
  'Add Opening Date': 'Display your opening date to make your business look authentic and establish credibility.',
  'Add primary category': 'Choose the right main category to help people find your business faster.',
  'Add secondary categories': 'Add supporting categories to reach a wider audience.',
  'Add business description': 'Describe your business to attract and engage potential customers.',
  'Add 5 Photos of premises': 'Showcase your space to build trust and visual appeal.',
  'Add some photos of products': 'Highlight your best products to attract more buyers.',
  'Add missing logo/cover photo': 'Add a logo and cover photo to strengthen your brand identity.',
  'Add Labels on your GBP': 'Organize your profile with labels for better insights and management.',
  'Upload interior/Exterior photos': 'Display your ambiance and location to impress visitors.',
  'Add missing attributes': 'Add details like "Wheelchair accessible" or "Wi-Fi available" to stand out.',
  'Reply to all pending reviews': 'Respond to reviews to build trust and customer loyalty.',
  'Generate more 5+ reviews': 'Encourage happy clients to share glowing feedback.',
  'Report fake/irrelevant reviews': 'Remove false reviews to protect your online reputation.',
  'Add missing services': 'List your services clearly so customers know what you offer.',
  'Update Existing services with descriptions': 'Enhance your listings with detailed service info.',
  'Add missing products': 'Add products to showcase your offerings and attract buyers.',
  'Add product descriptions': 'Write clear, catchy descriptions to increase product sales.',
  'Create service-related post': 'Promote your services with engaging posts to gain leads.',
  'Create product-offer post': 'Announce offers to attract more buyers and drive sales.',
  'Seasonal/holiday post': 'Celebrate seasons and festivals with themed posts to boost engagement.',
  'Limited-time discount/offer post': 'Share time-bound deals to create urgency and quick action.',
  'Event announcement post': 'Announce events to keep your customers updated and involved.',
  'Update outdated post captions': 'Refresh old captions to keep your content fresh and catchy.',
  'Fix website SSL issue': 'Secure your website to gain customer trust and SEO benefits.',
  'Update meta tags on website': 'Optimize meta tags for better visibility on Google Search.',
  'Add Social media Links': 'Connect your social profiles to boost credibility and engagement.'
};

// Get array of task titles for autocomplete
const PREDEFINED_TASK_TITLES = Object.keys(PREDEFINED_TASKS);

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: '6px',
  '&:hover': {
    backgroundColor: 'rgba(11, 145, 214, 0.1)'
  }
}));

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [gmbProfiles, setGmbProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [bulkAssignDialog, setBulkAssignDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [viewTasksDialog, setViewTasksDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileTasks, setProfileTasks] = useState([]);
  const [bulkAssignType, setBulkAssignType] = useState('all'); // 'all', 'selected_clients', 'selected_profiles'
  const [selectedClientsForBulk, setSelectedClientsForBulk] = useState([]);
  const [selectedProfilesForBulk, setSelectedProfilesForBulk] = useState([]);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [filePreview, setFilePreview] = useState([]);
  const [taskList, setTaskList] = useState([]);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);
  const [submitStatus, setSubmitStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [tasksPerPage] = useState(5);

  // Schedule Post Modal state
  const [schedulePostModalOpen, setSchedulePostModalOpen] = useState(false);
  const [capturedImageForSchedule, setCapturedImageForSchedule] = useState(null);
  const [schedulePostData, setSchedulePostData] = useState(null);

  // Task Catalog Modal state
  const [taskCatalogModalOpen, setTaskCatalogModalOpen] = useState(false);
  
  // AI generation states
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiFieldType, setAiFieldType] = useState(null); // 'description', 'categories', etc.

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignedTo: '',
    priority: 'medium',
    status: 'pending',
    assignDate: '',
    assignTime: '',
    category: '',
    estimatedHours: '',
    tags: '',
    attachments: [],
    // Real GMB field values for pre-filled solutions
    proposedSolution: '',
    proposedCategories: [],
    proposedDescription: '',
    proposedServices: [],
    proposedHours: {},
    proposedPhone: '',
    proposedWebsite: '',
    proposedAttributes: [],
    proposedLabels: [],
    proposedQandA: [],
    requiresApproval: false,
    autoApprove: false
  });

  useEffect(() => {
    fetchTasks();
    fetchClients();
    fetchGMBProfiles();
  }, []);

  const resetForm = () => {
    setUploadedFiles([]);
    setFilePreview([]);
    setTaskList([]);
    setCurrentTaskIndex(0);
    setFormData({
      title: '',
      description: '',
      assignedTo: '',
      priority: 'medium',
      status: 'pending',
      assignDate: '',
      assignTime: '',
      category: '',
      estimatedHours: '',
      tags: '',
      attachments: [],
      proposedSolution: '',
      proposedCategories: [],
      proposedDescription: '',
      proposedServices: [],
      proposedHours: {},
      proposedPhone: '',
      proposedWebsite: '',
      proposedAttributes: [],
      proposedLabels: [],
      proposedQandA: [],
      requiresApproval: false,
      autoApprove: false
    });
    setSelectedTask(null);
    setSelectedClient(null);
    setSelectedProfile(null);
    setBulkAssignType('all');
    setSelectedClientsForBulk([]);
    setSelectedProfilesForBulk([]);
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      console.log('📥 Fetching tasks from API...');
      const response = await ApiService.get('/admin/task/get-tasks');
      const tasksData = response?.data?.tasks || response?.tasks || [];
      
      console.log('📊 Raw tasks data:', tasksData);
      
      // Map backend data to frontend format
      const mappedTasks = tasksData.map(task => {
        // Build display name from client and profile info
        let displayName = 'Unassigned';
        
        console.log('🔍 Task mapping:', {
          taskId: task.id,
          title: task.title,
          hasClient: !!task.assignedClient,
          clientData: task.assignedClient,
          profileName: task.assigned_to_profile_name,
          clientId: task.assigned_to_client_id,
          profileId: task.assigned_to_profile_id
        });
        
        if (task.assignedClient) {
          const clientName = task.assignedClient.full_name || task.assignedClient.email;
          if (task.assigned_to_profile_name) {
            displayName = `${clientName} - ${task.assigned_to_profile_name}`;
          } else {
            displayName = clientName;
          }
        } else if (task.assigned_to_profile_name) {
          displayName = task.assigned_to_profile_name;
        }
        
        console.log('📝 Display name:', displayName);
        
        return {
          id: task.id,
          title: task.title,
          description: task.description,
          assignedTo: displayName,
          assignedToId: `${task.assigned_to_client_id}-${task.assigned_to_profile_id || ''}`,
          assignedToClientId: task.assigned_to_client_id,
          assignedToProfileId: task.assigned_to_profile_id,
          priority: task.priority,
          status: task.status,
          assignDate: task.assign_date,
          assignTime: task.assign_time,
          category: task.category,
          estimatedHours: task.estimated_hours,
          tags: task.tags,
          createdByType: task.created_by_type || 'admin',
          createdAt: task.created_at
        };
      });
      
      console.log(`✅ Loaded ${mappedTasks.length} tasks`);
      console.log('📋 Mapped tasks:', mappedTasks);
      setTasks(mappedTasks);
    } catch (error) {
      console.error('❌ Failed to fetch tasks:', error);
      showAlert('Failed to fetch tasks', 'error');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await ApiService.get('/admin/auth/get-customers');
      const clientData = response?.data || response || [];
      // Map to consistent format
      const mappedClients = clientData.map(client => ({
        id: client.id,
        name: client.full_name || client.name || client.email,
        email: client.email,
        user_id: client.id,
        userId: client.id
      }));
      setClients(mappedClients);
      console.log(`✅ Loaded ${mappedClients.length} clients`);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      showAlert('Failed to load clients', 'error');
      setClients([]);
    }
  };

  const fetchGMBProfiles = async () => {
    try {
      // Fetch ALL emails with ALL their GMBs directly from the admin endpoint
      const response = await ApiService.get('/gmb/admin/emails');
      const emailData = response?.data || response || [];
      
      if (!Array.isArray(emailData) || emailData.length === 0) {
        console.log('No emails with GMBs found');
        setGmbProfiles([]);
        return;
      }

      console.log(`📧 Found ${emailData.length} emails with GMBs`);

      // Map the response to the format expected by the Tasks component
      const profiles = emailData.map((entry, index) => {
        const email = entry.email || '';
        const gmbs = entry.gmbs || [];
        const userId = entry.userId || gmbs[0]?.userId || null;
        
        return {
          clientId: userId || (index + 1), // Use actual userId, fallback to index only if no userId
          clientName: email,
          clientEmail: email,
          userId: userId,
          profiles: gmbs.map(gmb => ({
            id: gmb.id || gmb.location_id,
            name: gmb.gmbName || gmb.business_name || gmb.name || 'Unnamed Business',
            locationId: gmb.id || gmb.location_id,
            businessName: gmb.gmbName || gmb.business_name,
            address: gmb.address || '',
            isVerified: gmb.isVerified || false,
            planTier: gmb.planTier || 'free'
          }))
        };
      });

      const profilesWithData = profiles.filter(p => p.profiles.length > 0);
      const totalGmbs = profiles.reduce((sum, p) => sum + p.profiles.length, 0);
      
      console.log(`📊 Summary: ${profiles.length} emails, ${profilesWithData.length} with GMBs, ${totalGmbs} total GMB profiles`);
      console.log(`📋 First 3 profiles:`, profiles.slice(0, 3));
      
      setGmbProfiles(profiles);
      
      if (profilesWithData.length === 0) {
        showAlert('No GMB profiles found.', 'info');
      }
    } catch (error) {
      console.error('❌ Failed to fetch GMB profiles:', error);
      showAlert('Failed to load GMB profiles. Please try again.', 'error');
      setGmbProfiles([]);
    }
  };

  const handleOpenDialog = (task = null) => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description,
        assignedTo: task.assignedToId,
        priority: task.priority,
        status: task.status,
        assignDate: task.assignDate,
        assignTime: task.assignTime,
        category: task.category,
        estimatedHours: task.estimatedHours,
        tags: task.tags
      });
      setSelectedTask(task);
    } else {
      setFormData({
        title: '',
        description: '',
        assignedTo: '',
        priority: 'medium',
        status: 'pending',
        assignDate: '',
        assignTime: '',
        category: '',
        estimatedHours: '',
        tags: ''
      });
      setSelectedTask(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTask(null);
    resetForm();
  };

  const handleViewTask = (task) => {
    setSelectedTask(task);
    setViewDialog(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Check if task requires file upload (photo/post related tasks)
  const requiresFileUpload = (title) => {
    const photoPostKeywords = ['photo', 'post', 'image', 'logo', 'cover', 'interior', 'exterior', 'premises', 'product'];
    return photoPostKeywords.some(keyword => title.toLowerCase().includes(keyword));
  };

  // Detect if task requires pre-filled solution (ALL tasks can have solutions now)
  const requiresPrefilledSolution = (title) => {
    // Allow pre-filled solutions for ALL tasks
    return title && title.trim().length > 0;
  };

  // Get solution field type from task title
  const getSolutionFieldType = (title) => {
    const titleLower = title.toLowerCase();
    
    // Specific field types for certain tasks
    if (titleLower.includes('description') || titleLower.includes('business info')) {
      return 'description';
    }
    if (titleLower.includes('categor')) {
      return 'categories';
    }
    if (titleLower.includes('service')) {
      return 'services';
    }
    if (titleLower.includes('hour') || titleLower.includes('timing')) {
      return 'hours';
    }
    if (titleLower.includes('phone') || titleLower.includes('contact')) {
      return 'contact';
    }
    if (titleLower.includes('website') || titleLower.includes('url') || titleLower.includes('link')) {
      return 'website';
    }
    if (titleLower.includes('attribute')) {
      return 'attributes';
    }
    if (titleLower.includes('label') || titleLower.includes('tag')) {
      return 'labels';
    }
    if (titleLower.includes('q&a') || titleLower.includes('question') || titleLower.includes('faq')) {
      return 'qanda';
    }
    
    // Generic solution for all other tasks
    return 'generic';
  };

  // Handle AI generation for solution fields
  const handleGenerateAISolution = async (fieldType) => {
    try {
      setGeneratingAI(true);
      setAiFieldType(fieldType);

      // Get the profile ID from assignedTo
      const assignedTo = formData.assignedTo;
      if (!assignedTo) {
        showAlert('Please select a profile first', 'warning');
        return;
      }

      const [clientId, profileId] = assignedTo.split('-');

      // Import AI service
      const { generateDescription, generateCategorySuggestions } = await import('../../../services/gmbRanking/geminiService');
      
      // Get business profile data
      const GMBService = (await import('../../../services/GMBService')).default;
      const accounts = await GMBService.getAccounts();
      const account = accounts?.[0];
      
      if (!account) {
        showAlert('No GMB account found', 'error');
        return;
      }

      const locations = await GMBService.getLocations(undefined, account.name);
      const location = locations?.find(loc => String(loc.name || '').split('/').pop() === String(profileId));

      if (!location) {
        showAlert('Profile location not found', 'error');
        return;
      }

      const businessName = location.title || location.name || 'Business';
      const currentCategory = location.categories?.primaryCategory?.displayName || '';

      if (fieldType === 'description') {
        const aiDescription = await generateDescription(businessName, currentCategory);
        setFormData(prev => ({
          ...prev,
          proposedDescription: aiDescription,
          requiresApproval: true
        }));
        showAlert('✨ AI description generated! Client will need to approve it.', 'success');
      } else if (fieldType === 'categories') {
        const suggestions = await generateCategorySuggestions(businessName);
        setFormData(prev => ({
          ...prev,
          proposedCategories: suggestions || [],
          requiresApproval: true
        }));
        showAlert('✨ AI categories generated! Client will need to approve them.', 'success');
      } else if (fieldType === 'services') {
        // Generate AI service suggestions (you can add this to openaiService later)
        const suggestions = await generateCategorySuggestions(businessName); // Reuse for now
        setFormData(prev => ({
          ...prev,
          proposedServices: suggestions || [],
          requiresApproval: true
        }));
        showAlert('✨ AI services generated! Client will need to approve them.', 'success');
      } else if (fieldType === 'labels') {
        // Generate AI label suggestions
        const { generateLabelSuggestions } = await import('../../../services/gmbRanking/geminiService');
        const suggestions = await generateLabelSuggestions(businessName, currentCategory);
        setFormData(prev => ({
          ...prev,
          proposedLabels: suggestions || [],
          requiresApproval: true
        }));
        showAlert('✨ AI labels generated! Client will need to approve them.', 'success');
      } else if (fieldType === 'qanda') {
        // Generate AI Q&A suggestions
        const { generateQnASuggestions } = await import('../../../services/gmbRanking/geminiService');
        const suggestions = await generateQnASuggestions(businessName, currentCategory);
        setFormData(prev => ({
          ...prev,
          proposedQandA: suggestions || [],
          requiresApproval: true
        }));
        showAlert('✨ AI Q&A generated! Client will need to approve them.', 'success');
      } else if (fieldType === 'generic') {
        // For generic tasks, generate a general solution suggestion
        const aiSolution = await generateDescription(businessName, currentCategory);
        setFormData(prev => ({
          ...prev,
          proposedSolution: `Suggested solution for "${formData.title}":\n\n${aiSolution}`,
          requiresApproval: true
        }));
        showAlert('✨ AI solution generated! Client will need to approve it.', 'success');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      showAlert('Failed to generate AI suggestion: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setGeneratingAI(false);
      setAiFieldType(null);
    }
  };

  // Handle task selection from catalog
  const handleSelectTaskFromCatalog = (task) => {
    // Update form data with task details
    setFormData(prev => ({
      ...prev,
      title: task.title || '',
      description: task.reason || '',
      category: task.category || '',
      priority: String(task.priority || 'medium').toLowerCase(),
    }));
    
    // Smart behavior: 
    // - If single profile dialog is open (openDialog=true), just update the form, stay in that dialog
    // - If no dialog is open, open bulk assign dialog
    if (!openDialog) {
      setBulkAssignDialog(true);
    }
    // If openDialog is already open, just update form (don't open bulk dialog)
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = [...uploadedFiles, ...files];
    setUploadedFiles(newFiles);

    // Create preview URLs for images
    const newPreviews = files.map(file => {
      if (file.type.startsWith('image/')) {
        return {
          name: file.name,
          url: URL.createObjectURL(file),
          type: 'image'
        };
      }
      return {
        name: file.name,
        url: null,
        type: file.type
      };
    });
    setFilePreview([...filePreview, ...newPreviews]);
  };

  // Remove uploaded file
  const handleRemoveFile = (index) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    const newPreviews = filePreview.filter((_, i) => i !== index);
    
    // Revoke object URL to free memory
    if (filePreview[index]?.url) {
      URL.revokeObjectURL(filePreview[index].url);
    }
    
    setUploadedFiles(newFiles);
    setFilePreview(newPreviews);
  };

  // Add current task to the task list
  const handleAddToList = () => {
    if (!formData.title || !formData.description || !formData.assignedTo) {
      showAlert('Please fill in all required fields', 'error');
      return;
    }

    const newTask = {
      ...formData,
      attachments: [...uploadedFiles],
      previews: [...filePreview],
      id: Date.now() // Temporary ID for list management
    };

    setTaskList([...taskList, newTask]);
    
    // Reset form for next task but keep assignedTo, assignDate, assignTime
    const keepValues = {
      assignedTo: formData.assignedTo,
      assignDate: formData.assignDate,
      assignTime: formData.assignTime,
      priority: formData.priority
    };
    
    setFormData({
      title: '',
      description: '',
      ...keepValues,
      status: 'pending',
      category: '',
      estimatedHours: '',
      tags: '',
      attachments: []
    });
    
    setUploadedFiles([]);
    setFilePreview([]);
    
    showAlert(`Task added to list! (${taskList.length + 1} task${taskList.length + 1 > 1 ? 's' : ''} ready)`, 'success');
  };

  // Remove task from list
  const handleRemoveFromList = (taskId) => {
    const updatedList = taskList.filter(task => task.id !== taskId);
    setTaskList(updatedList);
    showAlert('Task removed from list', 'info');
  };

  // Submit all tasks in the list
  const handleSubmitAll = async () => {
    if (taskList.length === 0) {
      showAlert('No tasks in the list to submit', 'error');
      return;
    }

    try {
      setLoading(true);
      setSubmitting(true);
      setSubmitProgress(0);
      setSubmitStatus('Preparing to submit tasks...');
      
      let successCount = 0;
      let failCount = 0;
      const totalTasks = taskList.length;

      for (let i = 0; i < taskList.length; i++) {
        const task = taskList[i];
        setSubmitStatus(`Submitting task ${i + 1} of ${totalTasks}: ${task.title}`);
        try {
          // Parse the assignedTo value
          const [clientId, profileId] = task.assignedTo.toString().split('-');
          
          // Find the GMB profile name
          let profileName = '';
          for (const clientProfile of gmbProfiles) {
            if (clientProfile.clientId.toString() === clientId) {
              const profile = clientProfile.profiles.find(p => p.id === profileId);
              if (profile) {
                profileName = profile.name;
                break;
              }
            }
          }

          // Get timezone offset in minutes
          const timezoneOffset = -new Date().getTimezoneOffset();

          const taskData = {
            title: task.title,
            description: task.description,
            assignedTo: task.assignedTo,
            profileName: profileName,
            priority: task.priority,
            status: task.status,
            assignDate: task.assignDate,
            assignTime: task.assignTime,
            timezoneOffset: timezoneOffset, // Add timezone offset
            category: task.category,
            estimatedHours: task.estimatedHours,
            tags: task.tags,
            hasAttachments: task.attachments.length > 0
          };

          // Prepare request data with files if any
          let requestData;
          if (task.attachments.length > 0) {
            requestData = new FormData();
            Object.keys(taskData).forEach(key => {
              requestData.append(key, taskData[key]);
            });
            task.attachments.forEach((file) => {
              requestData.append('attachments', file);
            });
          } else {
            requestData = taskData;
          }

          const response = await ApiService.post('/admin/task/create-task', requestData);
          
          if (response.success) {
            successCount++;
            setSubmitStatus(`✅ Task ${i + 1} created successfully!`);
          } else {
            failCount++;
            setSubmitStatus(`❌ Task ${i + 1} failed`);
          }
        } catch (error) {
          console.error('Error creating task:', error);
          failCount++;
          setSubmitStatus(`❌ Task ${i + 1} failed`);
        }
        
        // Update progress
        setSubmitProgress(((i + 1) / totalTasks) * 100);
        
        // Small delay to show progress
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      setSubmitStatus('All tasks submitted!');
      
      if (successCount > 0) {
        showAlert(`✅ ${successCount} task${successCount > 1 ? 's' : ''} created successfully!${failCount > 0 ? ` (${failCount} failed)` : ''}`, 'success');
        fetchTasks();
        
        // Get profile info from first task to show tasks
        const firstTask = taskList[0];
        const [clientId, profileId] = firstTask.assignedTo.toString().split('-');
        
        // Find client and profile names
        let clientName = '';
        let profileName = '';
        for (const clientProfile of gmbProfiles) {
          if (clientProfile.clientId.toString() === clientId) {
            clientName = clientProfile.clientName;
            const profile = clientProfile.profiles.find(p => p.id === profileId);
            if (profile) {
              profileName = profile.name;
              break;
            }
          }
        }
        
        // Close dialog and open view tasks dialog after short delay
        setTimeout(() => {
          // Close create dialog
          setOpenDialog(false);
          setSelectedTask(null);
          resetForm();
          setSubmitting(false);
          
          // Small delay to ensure tasks are saved before fetching
          setTimeout(() => {
            // Open view tasks dialog to show the created tasks
            if (clientId && profileId) {
              console.log('🔍 Opening view tasks dialog for:', clientName, profileName);
              handleViewTasksForProfile(clientId, profileId, clientName, profileName);
            }
          }, 500);
        }, 1500);
      } else {
        showAlert('Failed to create tasks', 'error');
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Error submitting tasks:', error);
      showAlert('Failed to submit tasks', 'error');
      setSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      // Validate required fields
      if (!formData.title || !formData.title.trim()) {
        showAlert('Please enter a task title', 'error');
        return;
      }
      
      if (!formData.assignedTo) {
        showAlert('Please select a client and profile to assign the task', 'error');
        return;
      }
      
      setLoading(true);
      setSubmitting(true);
      setSubmitProgress(0);
      setSubmitStatus('Creating task...');
      
      // Parse the assignedTo value (format: "clientId-profileId")
      const [clientId, profileId] = formData.assignedTo.toString().split('-');
      
      // Find the GMB profile name
      let profileName = '';
      let clientName = '';
      for (const clientProfile of gmbProfiles) {
        if (clientProfile.clientId.toString() === clientId) {
          clientName = clientProfile.clientName;
          const profile = clientProfile.profiles.find(p => p.id === profileId);
          if (profile) {
            profileName = profile.name;
            break;
          }
        }
      }
      
      const displayName = profileName ? `${clientName} - ${profileName}` : clientName;

      // Step 1: Upload files if any
      let uploadedFilePaths = [];
      if (uploadedFiles.length > 0) {
        console.log(`📤 Uploading ${uploadedFiles.length} file(s)...`);
        setSubmitStatus('Uploading files...');
        setSubmitProgress(20);

        const formData = new FormData();
        uploadedFiles.forEach(file => {
          formData.append('attachments', file);
        });

        try {
          const uploadResponse = await ApiService.post('/admin/task/upload-attachments', formData);
          if (uploadResponse.success) {
            uploadedFilePaths = uploadResponse.files || [];
            console.log(`✅ Uploaded ${uploadedFilePaths.length} file(s):`, uploadedFilePaths);
          }
        } catch (uploadError) {
          console.error('❌ File upload failed:', uploadError);
          showAlert('Failed to upload attachments. Task will be created without files.', 'warning');
        }
      }

      setSubmitProgress(30);
      setSubmitStatus('Creating task...');

      // Step 2: Prepare data for backend with uploaded file paths
      // Get timezone offset in minutes (e.g., IST = +330, EST = -300)
      const timezoneOffset = -new Date().getTimezoneOffset();
      
      const taskData = {
        title: formData.title,
        description: formData.description,
        assignedTo: formData.assignedTo,
        profileName: profileName,
        priority: formData.priority,
        status: formData.status,
        assignDate: formData.assignDate,
        assignTime: formData.assignTime,
        timezoneOffset: timezoneOffset, // Add timezone offset for accurate scheduling
        category: formData.category,
        estimatedHours: formData.estimatedHours,
        tags: formData.tags,
        attachments: uploadedFilePaths, // Include uploaded file paths
        // Real GMB field values for pre-filled solutions (approval workflow)
        proposedSolution: formData.proposedSolution || '',
        proposedDescription: formData.proposedDescription || '',
        proposedCategories: formData.proposedCategories || [],
        proposedServices: formData.proposedServices || [],
        proposedPhone: formData.proposedPhone || '',
        proposedWebsite: formData.proposedWebsite || '',
        proposedLabels: formData.proposedLabels || [],
        proposedQandA: formData.proposedQandA || [],
        requiresApproval: formData.autoApprove ? false : !!formData.requiresApproval,
        autoApprove: !!formData.autoApprove
      };
      
      console.log(`🌍 Timezone offset: ${timezoneOffset} minutes (${timezoneOffset / 60} hours from UTC)`);
      console.log('🔍 [Admin] Task data being sent to backend:', {
        title: taskData.title,
        proposedDescription: taskData.proposedDescription,
        requiresApproval: taskData.requiresApproval,
        fullTaskData: taskData
      });
      
      if (selectedTask) {
        // Update existing task
        setSubmitStatus('Updating task...');
        console.log('Updating task:', selectedTask.id, taskData);
        const response = await ApiService.put(`/admin/task/update-task/${selectedTask.id}`, taskData);
        
        console.log('📥 Task update response:', response);
        
        setSubmitProgress(100);
        // Check for success in multiple ways
        if (response.success || response.data?.success || response.message?.includes('successfully')) {
          setSubmitStatus('✅ Task updated successfully!');
          showAlert('Task updated successfully', 'success');
          fetchTasks(); // Refresh task list
        } else {
          throw new Error(response.message || response.error || 'Failed to update task');
        }
      } else {
        // Create new task
        setSubmitStatus('Submitting task...');
        console.log('Creating task:', taskData);
        const response = await ApiService.post('/admin/task/create-task', taskData);
        
        console.log('📥 Task creation response:', response);
        
        setSubmitProgress(100);
        // Check for success in multiple ways (API might return success differently)
        if (response.success || response.data?.success || response.message?.includes('successfully')) {
          setSubmitStatus('✅ Task created successfully!');
          showAlert(`Task created successfully! ${uploadedFiles.length > 0 ? `📎 ${uploadedFiles.length} file(s) attached` : '🎉'}`, 'success');
          fetchTasks(); // Refresh task list
        } else {
          throw new Error(response.message || response.error || 'Failed to create task');
        }
      }
      
      // Close after short delay and open view tasks dialog
      setTimeout(() => {
        // Close create dialog
        setOpenDialog(false);
        setSelectedTask(null);
        resetForm();
        setSubmitting(false);
        
        // Small delay to ensure task is saved before fetching
        setTimeout(() => {
          // Open view tasks dialog to show the created task
          if (clientId && profileId) {
            console.log('🔍 Opening view tasks dialog for:', clientName, profileName);
            handleViewTasksForProfile(clientId, profileId, clientName, profileName);
          }
        }, 500);
      }, 1000);
    } catch (error) {
      console.error('Error saving task:', error);
      showAlert(error.message || 'Failed to save task', 'error');
      setSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  // Schedule Post Handlers
  const handleOpenSchedulePost = () => {
    // Check if this is a photo/post/image task
    const isPostTask = formData.category && 
      (formData.category.toLowerCase().includes('photo') || 
       formData.category.toLowerCase().includes('post') || 
       formData.category.toLowerCase().includes('image'));
    
    if (!isPostTask) {
      showAlert('Please set category to Photo, Post, or Image to schedule a post', 'warning');
      return;
    }

    if (!formData.assignedTo) {
      showAlert('Please select a GMB profile first', 'warning');
      return;
    }

    if (!formData.title || !formData.title.trim()) {
      showAlert('Please enter task title first', 'warning');
      return;
    }

    // Get account and location IDs from assignedTo
    const [clientId, locationId] = formData.assignedTo.split('-');
    
    console.log('📅 Opening Schedule Post Modal:', { clientId, locationId });
    
    // If there are uploaded images, use the first one
    if (uploadedFiles.length > 0 && uploadedFiles[0].type.startsWith('image/')) {
      setCapturedImageForSchedule(uploadedFiles[0]); // Pass File object directly
      console.log('🖼️ Using uploaded image:', uploadedFiles[0].name);
    } else {
      setCapturedImageForSchedule(null);
    }
    
    setSchedulePostData({
      clientId,
      locationId,
      taskTitle: formData.title,
      taskDescription: formData.description
    });
    setSchedulePostModalOpen(true);
  };

  const handleCloseSchedulePostModal = () => {
    setSchedulePostModalOpen(false);
    setCapturedImageForSchedule(null);
    setSchedulePostData(null);
  };

  const handleSchedulePostSuccess = async (postData) => {
    console.log('📝 Post scheduled successfully:', postData);
    console.log('📸 Media data received:', postData.media);
    
    // Store post content with the task
    const postContent = {
      summary: postData.summary,
      topicType: postData.topicType,
      callToAction: postData.callToAction,
      media: postData.media,
      scheduledFor: postData.scheduledFor
    };
    
    console.log('💾 Post content to save:', {
      ...postContent,
      media: postContent.media?.map(m => ({
        mediaFormat: m.mediaFormat,
        hasSourceUrl: !!m.sourceUrl,
        sourceUrlLength: m.sourceUrl?.length || 0,
        hasFile: !!m.file
      }))
    });

    // Save task with post content
    try {
      setLoading(true);
      setSubmitProgress(10);
      setSubmitStatus('Creating task with scheduled post...');
      
      // Get timezone offset
      const timezoneOffset = -new Date().getTimezoneOffset();
      
      // Extract profile info
      const [clientId, profileId] = formData.assignedTo.split('-');
      
      // Find profile name
      let profileName = '';
      for (const clientProfile of gmbProfiles) {
        if (clientProfile.clientId.toString() === clientId) {
          const profile = clientProfile.profiles.find(p => p.id === profileId);
          if (profile) {
            profileName = profile.name;
            break;
          }
        }
      }
      
      const taskData = {
        title: formData.title,
        description: formData.description,
        assignedTo: formData.assignedTo,
        profileName: profileName,
        priority: formData.priority,
        status: formData.status,
        assignDate: formData.assignDate,
        assignTime: formData.assignTime,
        timezoneOffset: timezoneOffset,
        category: formData.category,
        estimatedHours: formData.estimatedHours,
        tags: formData.tags,
        post_content: JSON.stringify(postContent),
        post_status: 'pending',
        attachments: [],
        requiresApproval: formData.autoApprove ? false : !!formData.requiresApproval,
        autoApprove: !!formData.autoApprove
      };

      setSubmitProgress(50);
      console.log('Creating task with post content:', taskData);
      console.log('🔍 Auto-approve flag being sent:', {
        autoApprove: taskData.autoApprove,
        requiresApproval: taskData.requiresApproval
      });
      const response = await ApiService.post('/admin/task/create-task', taskData);
      
      console.log('📥 Task creation response:', response);
      console.log('📥 Auto-approve result:', response?.data?.autoApproveResult || response?.autoApproveResult);
      
      setSubmitProgress(100);
      
      // Check for success in multiple ways
      const isSuccess = response.success || 
                       response.data?.success || 
                       response.message?.toLowerCase().includes('success') ||
                       response.data?.task?.id;
      
      if (isSuccess) {
        setSubmitStatus('✅ Task created successfully!');
        
        // Check auto-approve result
        const autoApproveResult = response?.data?.autoApproveResult || response?.autoApproveResult;
        if (autoApproveResult) {
          if (autoApproveResult.success) {
            showAlert('✅ Task created and auto-approved! Post is now scheduled on client\'s GMB. 🎉', 'success');
          } else {
            showAlert(`⚠️ Task created but auto-approve failed: ${autoApproveResult.message}. Client will need to approve manually.`, 'warning');
          }
        } else if (taskData.autoApprove) {
          showAlert('⚠️ Task created but auto-approve status unknown. Check backend logs.', 'warning');
        } else {
          showAlert('Task with scheduled post created successfully! Client can now approve it. 🎉', 'success');
        }
        
        // Close modals and reset
        setTimeout(() => {
          handleCloseSchedulePostModal();
          handleCloseDialog();
          fetchTasks();
          setSubmitProgress(0);
          setSubmitStatus('');
        }, 1000);
      } else {
        throw new Error('Failed to create task - unexpected response format');
      }
    } catch (error) {
      console.error('Error creating task with post:', error);
      showAlert(error.message || 'Failed to create task with post', 'error');
      setSubmitProgress(0);
      setSubmitStatus('');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        setLoading(true);
        console.log('Deleting task:', taskId);
        
        const response = await ApiService.delete(`/admin/task/delete-task/${taskId}`);
        
        if (response.success) {
          showAlert('Task deleted successfully', 'success');
          fetchTasks(); // Refresh task list
        } else {
          throw new Error(response.message || 'Failed to delete task');
        }
      } catch (error) {
        console.error('Error deleting task:', error);
        showAlert(error.message || 'Failed to delete task', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleVisibility = async (taskId, currentVisibility) => {
    try {
      console.log(`👁️ Toggling visibility for task ${taskId}: ${!currentVisibility}`);
      
      const response = await ApiService.put(`/admin/task/toggle-visibility/${taskId}`, {
        isVisible: !currentVisibility
      });
      
      if (response.success) {
        showAlert(
          `Task ${!currentVisibility ? 'shown to' : 'hidden from'} client`,
          'success'
        );
        // Refresh the tasks in the dialog
        if (selectedClient && selectedProfile) {
          handleViewTasksForProfile(
            selectedClient.id,
            selectedProfile.id,
            selectedClient.name,
            selectedProfile.name
          );
        }
      } else {
        throw new Error(response.message || 'Failed to toggle visibility');
      }
    } catch (error) {
      console.error('Error toggling visibility:', error);
      showAlert(error.message || 'Failed to toggle visibility', 'error');
    }
  };

  const handleBulkAssignTask = async () => {
    try {
      setLoading(true);
      
      // Step 1: Upload files if any
      let uploadedFilePaths = [];
      if (uploadedFiles.length > 0) {
        console.log(`📤 Uploading ${uploadedFiles.length} file(s)...`);
        
        const formData = new FormData();
        uploadedFiles.forEach(file => {
          formData.append('attachments', file);
        });

        try {
          const uploadResponse = await ApiService.post('/admin/task/upload-attachments', formData);
          if (uploadResponse.success) {
            uploadedFilePaths = uploadResponse.files || [];
            console.log(`✅ Uploaded ${uploadedFilePaths.length} file(s):`, uploadedFilePaths);
          }
        } catch (uploadError) {
          console.error('❌ File upload failed:', uploadError);
          showAlert('Failed to upload attachments. Task will be created without files.', 'warning');
        }
      }
      
      // Step 2: Create task with attachments
      const requestData = {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        status: formData.status,
        category: formData.category,
        estimatedHours: formData.estimatedHours,
        assignDate: formData.assignDate,
        assignTime: formData.assignTime,
        tags: formData.tags,
        attachments: uploadedFilePaths, // Include uploaded file paths
        assignToAll: bulkAssignType === 'all',
        selectedClients: bulkAssignType === 'selected_clients' ? selectedClientsForBulk : [],
        selectedProfiles: bulkAssignType === 'selected_profiles' ? selectedProfilesForBulk : []
      };

      console.log('📋 Bulk assign request:', requestData);

      const response = await ApiService.post('/admin/task/bulk-assign-task', requestData);

      if (response.success || response.error === null) {
        const createdCount = response.data?.createdCount || response.createdCount || 0;
        showAlert(
          `Successfully assigned task to ${createdCount} profiles${uploadedFilePaths.length > 0 ? ` with ${uploadedFilePaths.length} attachment(s)` : ''}!`,
          'success'
        );
        setBulkAssignDialog(false);
        resetForm();
        fetchTasks();
      } else {
        throw new Error(response.message || 'Failed to bulk assign task');
      }
    } catch (error) {
      console.error('Error bulk assigning task:', error);
      showAlert(error.message || 'Failed to bulk assign task', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTaskForProfile = (clientId, profileId, clientName, profileName) => {
    setSelectedClient({ id: clientId, name: clientName });
    setSelectedProfile({ id: profileId, name: profileName });
    setFormData({
      title: '',
      description: '',
      assignedTo: `${clientId}-${profileId}`,
      priority: 'medium',
      status: 'pending',
      assignDate: '',
      assignTime: '',
      category: '',
      estimatedHours: '',
      tags: ''
    });
    setSelectedTask(null);
    setOpenDialog(true);
  };

  const handleViewTasksForProfile = async (clientId, profileId, clientName, profileName) => {
    setSelectedClient({ id: clientId, name: clientName });
    setSelectedProfile({ id: profileId, name: profileName });
    
    // IMPORTANT: Clear previous tasks immediately to prevent showing wrong profile's data
    setProfileTasks([]);
    console.log(`🗑️ [ADMIN VIEW] Cleared previous profile tasks before loading new profile: ${profileId}`);
    
    setLoading(true);
    
    try {
      console.log('═══════════════════════════════════════════════════════');
      console.log(`📋 [ADMIN VIEW] Fetching ALL tasks for profile: ${profileId}`);
      console.log('═══════════════════════════════════════════════════════');
      
      // 1. Fetch admin-assigned tasks from database
      const response = await ApiService.get('/admin/task/get-tasks');
      const tasksData = response?.data?.tasks || response?.tasks || [];
      
      console.log(`📊 [ADMIN VIEW] Total tasks in database: ${tasksData.length}`);
      console.log(`📋 [ADMIN VIEW] All tasks:`, tasksData);
      
      // Extract numeric ID from location_id (remove "locations/" prefix if present)
      const numericProfileId = profileId.toString().replace('locations/', '');
      
      console.log(`🔍 [ADMIN VIEW] Filtering tasks by profileId: ${profileId} or numericId: ${numericProfileId}`);
      
      // Filter admin tasks for this specific profile
      const adminTasks = tasksData.filter(task => {
        const taskProfileId = task.assigned_to_profile_id?.toString() || '';
        const taskNumericId = taskProfileId.replace('locations/', '');
        
        const matches = taskProfileId === profileId || 
               taskProfileId === numericProfileId ||
               taskNumericId === numericProfileId;
        
        if (matches) {
          console.log(`✅ [ADMIN VIEW] Task matches profile:`, {
            taskId: task.id,
            taskTitle: task.title,
            taskProfileId: taskProfileId,
            createdByType: task.created_by_type
          });
        }
        
        return matches;
      });
      
      // Map admin tasks to display format
      const mappedAdminTasks = adminTasks.map(task => ({
        id: task.id,
        title: task.title,
        description: task.description,
        priority: task.priority,
        status: task.status,
        assignDate: task.assign_date,
        assignTime: task.assign_time,
        category: task.category,
        estimatedHours: task.estimated_hours,
        tags: task.tags,
        createdByType: task.created_by_type || 'admin',
        createdAt: task.created_at,
        isVisibleToClient: task.is_visible_to_client !== false // Default to true
      }));
      
      console.log(`✅ [ADMIN VIEW] Found ${mappedAdminTasks.length} tasks from database`);
      console.log(`📊 [ADMIN VIEW] Task breakdown:`, {
        total: mappedAdminTasks.length,
        admin: mappedAdminTasks.filter(t => t.createdByType === 'admin').length,
        ai: mappedAdminTasks.filter(t => t.createdByType === 'ai').length,
        system: mappedAdminTasks.filter(t => t.createdByType === 'system').length
      });
      
      // IMPORTANT: Admin ONLY loads from database (not localStorage)
      // All tasks (admin + AI) are now saved in database
      console.log('✅ [ADMIN VIEW] Using database tasks only (localStorage ignored for admin)');
      console.log('═══════════════════════════════════════════════════════');
      
      setProfileTasks(mappedAdminTasks);
      setViewTasksDialog(true);
    } catch (error) {
      console.error('Error fetching profile tasks:', error);
      showAlert('Failed to load tasks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, severity) => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 3000);
  };

  // Filter tasks based on search query
  const filteredTasks = tasks.filter(task => {
    const searchLower = searchQuery.toLowerCase();
    return (
      task.title?.toLowerCase().includes(searchLower) ||
      task.description?.toLowerCase().includes(searchLower) ||
      task.assignedTo?.toLowerCase().includes(searchLower) ||
      task.status?.toLowerCase().includes(searchLower) ||
      task.priority?.toLowerCase().includes(searchLower) ||
      task.category?.toLowerCase().includes(searchLower)
    );
  });

  // Filter GMB profiles based on search query (searches across ALL profiles)
  // ONLY show clients that have at least one profile with permissions
  const filteredProfiles = gmbProfiles
    .filter(clientProfile => clientProfile.profiles.length > 0) // Only clients with profiles
    .filter(clientProfile => {
      if (!searchQuery || searchQuery.trim() === '') return true; // Show all if no search
      
      const searchLower = searchQuery.toLowerCase();
      
      // Search in client name and email
      const clientMatch = 
        clientProfile.clientName?.toLowerCase().includes(searchLower) ||
        clientProfile.clientEmail?.toLowerCase().includes(searchLower);
      
      // Search in profile names
      const profileMatch = clientProfile.profiles.some(profile =>
        profile.name?.toLowerCase().includes(searchLower) ||
        profile.businessName?.toLowerCase().includes(searchLower) ||
        profile.address?.toLowerCase().includes(searchLower) ||
        profile.category?.toLowerCase().includes(searchLower)
      );
      
      return clientMatch || profileMatch;
    });
  
  // Debug log for filtered profiles
  console.log(`🔍 Filtered profiles: ${filteredProfiles.length} of ${gmbProfiles.length} (search: "${searchQuery}")`);

  // Pagination logic for filtered profiles
  const indexOfLastTask = currentPage * tasksPerPage;
  const indexOfFirstTask = indexOfLastTask - tasksPerPage;
  const currentTasks = filteredTasks.slice(indexOfFirstTask, indexOfLastTask);
  const currentProfiles = filteredProfiles.slice(indexOfFirstTask, indexOfLastTask);
  const totalPages = Math.ceil(filteredProfiles.length / tasksPerPage);

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'info';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  return (
    <PageContainer>
      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <PageHeader>
        <Typography variant="h4" fontWeight={700} color="#2c3e50">
          Task Management - Clients & GMB Profiles
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={handleSearchChange}
            size="small"
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<LibraryBooks />}
            onClick={() => setTaskCatalogModalOpen(true)}
            sx={{
              borderColor: '#667eea',
              color: '#667eea',
              '&:hover': {
                borderColor: '#764ba2',
                backgroundColor: 'rgba(102, 126, 234, 0.04)'
              }
            }}
          >
            Task Catalog
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              resetForm();
              setBulkAssignDialog(true);
            }}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
            }
          }}
        >
          Bulk Assign Task
        </Button>
        </Box>
      </PageHeader>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Tasks
              </Typography>
              <Typography variant="h4" fontWeight={700}>
                {filteredTasks.length}
                {searchQuery && <Typography variant="caption" display="block" color="textSecondary">of {tasks.length}</Typography>}
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {filteredTasks.filter(t => t.status === 'pending').length}
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                In Progress
              </Typography>
              <Typography variant="h4" fontWeight={700} color="info.main">
                {filteredTasks.filter(t => t.status === 'in-progress').length}
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed
              </Typography>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {filteredTasks.filter(t => t.status === 'completed').length}
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Client & GMB Profile List */}
      {console.log('🎨 Rendering profiles section:', { loading, gmbProfilesLength: gmbProfiles.length, filteredProfilesLength: filteredProfiles.length, currentProfilesLength: currentProfiles.length })}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : gmbProfiles.length === 0 ? (
        <StyledCard>
          <CardContent>
            <Typography color="textSecondary" align="center">
              No clients or GMB profiles found
            </Typography>
          </CardContent>
        </StyledCard>
      ) : filteredProfiles.length === 0 ? (
        <StyledCard>
          <CardContent>
            <Typography color="textSecondary" align="center">
              No profiles found matching "{searchQuery}"
            </Typography>
          </CardContent>
        </StyledCard>
      ) : (
        <>
          {currentProfiles.map((clientProfile) => (
            <StyledCard key={clientProfile.clientId} sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" fontWeight={700} color="#2c3e50">
                    {clientProfile.clientName}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {clientProfile.clientEmail}
                  </Typography>
                </Box>
                
                {clientProfile.profiles.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                    No GMB profiles available
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {clientProfile.profiles.map((profile) => (
                    <Grid item xs={12} md={6} key={profile.id}>
                      <Paper 
                        elevation={1} 
                        sx={{ 
                          p: 2, 
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          '&:hover': { boxShadow: 3 }
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                          {profile.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Add />}
                            onClick={() => handleCreateTaskForProfile(
                              clientProfile.clientId,
                              profile.id,
                              clientProfile.clientName,
                              profile.name
                            )}
                            sx={{
                              backgroundColor: '#0b91d6',
                              textTransform: 'none',
                              '&:hover': { backgroundColor: '#0a7bb8' }
                            }}
                          >
                            Create Task
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => handleViewTasksForProfile(
                              clientProfile.clientId,
                              profile.id,
                              clientProfile.clientName,
                              profile.name
                            )}
                            sx={{
                              borderColor: '#0b91d6',
                              color: '#0b91d6',
                              textTransform: 'none',
                              '&:hover': { borderColor: '#0a7bb8', backgroundColor: '#f0f8ff' }
                            }}
                          >
                            View Tasks
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </StyledCard>
          ))}
          
          {/* Pagination */}
          {filteredProfiles.length > tasksPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <TablePagination
                component="div"
                count={filteredProfiles.length}
                page={currentPage - 1}
                onPageChange={(e, page) => handlePageChange(e, page + 1)}
                rowsPerPage={tasksPerPage}
                rowsPerPageOptions={[5]}
                labelRowsPerPage="Profiles per page:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} of ${count}${searchQuery ? ` (filtered from ${gmbProfiles.length})` : ''}`
                }
              />
            </Box>
          )}
        </>
      )}

      {/* Create/Edit Task Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              {selectedTask ? 'Edit Task' : 'Create New Task'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<LibraryBooks />}
                onClick={() => setTaskCatalogModalOpen(true)}
                sx={{
                  borderColor: '#667eea',
                  color: '#667eea',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#764ba2',
                    backgroundColor: 'rgba(102, 126, 234, 0.04)'
                  }
                }}
              >
                Browse Catalog
              </Button>
              <IconButton onClick={handleCloseDialog} size="small">
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={PREDEFINED_TASK_TITLES}
                value={formData.title}
                onChange={(event, newValue) => {
                  // Auto-fill description when a predefined task is selected
                  const description = PREDEFINED_TASKS[newValue] || formData.description;
                  setFormData({ 
                    ...formData, 
                    title: newValue || '',
                    description: newValue && PREDEFINED_TASKS[newValue] ? description : formData.description
                  });
                }}
                onInputChange={(event, newInputValue) => {
                  setFormData({ ...formData, title: newInputValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Task Title"
                    name="title"
                    required
                    placeholder="Select or type a task title"
                    helperText="Select from predefined tasks to auto-fill description"
                    error={!formData.title && submitting}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: 2, backgroundColor: '#fafafa' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Approval Mode
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formData.autoApprove}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        autoApprove: e.target.checked,
                        requiresApproval: !e.target.checked
                      }))}
                    />
                  }
                  label={formData.autoApprove ? 'Auto-approve and apply (skip client approval)' : 'Manual approval by client'}
                />
              </Box>
            </Grid>

            {/* Smart Pre-filled Solution Section */}
            {requiresPrefilledSolution(formData.title) && formData.assignedTo && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ 
                    border: '2px solid #667eea', 
                    borderRadius: '8px', 
                    padding: '16px', 
                    backgroundColor: '#f8f9ff'
                  }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, color: '#667eea' }}>
                      💡 Pre-fill Solution for Client Approval
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Fill in the solution below. Client will only need to approve or reject it.
                    </Typography>

                    {getSolutionFieldType(formData.title) === 'description' && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Proposed Business Description
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleGenerateAISolution('description')}
                            disabled={generatingAI}
                            startIcon={generatingAI && aiFieldType === 'description' ? <CircularProgress size={16} /> : null}
                            sx={{
                              ml: 'auto',
                              borderColor: '#667eea',
                              color: '#667eea',
                              '&:hover': {
                                borderColor: '#764ba2',
                                backgroundColor: 'rgba(102, 126, 234, 0.04)'
                              }
                            }}
                          >
                            {generatingAI && aiFieldType === 'description' ? 'Generating...' : '✨ Generate with AI'}
                          </Button>
                        </Box>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={formData.proposedDescription}
                          onChange={(e) => setFormData({ ...formData, proposedDescription: e.target.value, requiresApproval: true })}
                          placeholder="Write the business description here or use AI to generate it..."
                          variant="outlined"
                        />
                      </Box>
                    )}

                    {getSolutionFieldType(formData.title) === 'categories' && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Proposed Categories
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleGenerateAISolution('categories')}
                            disabled={generatingAI}
                            startIcon={generatingAI && aiFieldType === 'categories' ? <CircularProgress size={16} /> : null}
                            sx={{
                              ml: 'auto',
                              borderColor: '#667eea',
                              color: '#667eea',
                              '&:hover': {
                                borderColor: '#764ba2',
                                backgroundColor: 'rgba(102, 126, 234, 0.04)'
                              }
                            }}
                          >
                            {generatingAI && aiFieldType === 'categories' ? 'Generating...' : '✨ Generate with AI'}
                          </Button>
                        </Box>
                        <Autocomplete
                          multiple
                          freeSolo
                          options={SORTED_GMB_CATEGORIES}
                          value={formData.proposedCategories || []}
                          onChange={(event, newValue) => {
                            setFormData({ 
                              ...formData, 
                              proposedCategories: newValue,
                              requiresApproval: true 
                            });
                          }}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                key={index}
                                label={option}
                                {...getTagProps({ index })}
                                sx={{ bgcolor: '#667eea', color: 'white' }}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Type to search or select categories..."
                              helperText="Select from predefined categories or type custom ones"
                            />
                          )}
                          sx={{
                            '& .MuiAutocomplete-tag': {
                              bgcolor: '#667eea',
                              color: 'white'
                            }
                          }}
                        />
                      </Box>
                    )}

                    {getSolutionFieldType(formData.title) === 'services' && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Proposed Services
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleGenerateAISolution('services')}
                            disabled={generatingAI}
                            startIcon={generatingAI && aiFieldType === 'services' ? <CircularProgress size={16} /> : null}
                            sx={{
                              ml: 'auto',
                              borderColor: '#667eea',
                              color: '#667eea',
                              '&:hover': {
                                borderColor: '#764ba2',
                                backgroundColor: 'rgba(102, 126, 234, 0.04)'
                              }
                            }}
                          >
                            {generatingAI && aiFieldType === 'services' ? 'Generating...' : '✨ Generate with AI'}
                          </Button>
                        </Box>
                        <Autocomplete
                          multiple
                          freeSolo
                          options={SORTED_GMB_SERVICES}
                          value={formData.proposedServices || []}
                          onChange={(event, newValue) => {
                            setFormData({ 
                              ...formData, 
                              proposedServices: newValue,
                              requiresApproval: true 
                            });
                          }}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                key={index}
                                label={option}
                                {...getTagProps({ index })}
                                sx={{ bgcolor: '#667eea', color: 'white', mb: 0.5 }}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Type to search or select services..."
                              helperText="Select from predefined services or type custom ones"
                            />
                          )}
                        />
                      </Box>
                    )}

                    {getSolutionFieldType(formData.title) === 'website' && (
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                          Proposed Website URL
                        </Typography>
                        <TextField
                          fullWidth
                          type="url"
                          value={formData.proposedWebsite || ''}
                          onChange={(e) => setFormData({ ...formData, proposedWebsite: e.target.value, requiresApproval: true })}
                          placeholder="https://example.com"
                          variant="outlined"
                          helperText="Enter the full website URL including https://"
                        />
                      </Box>
                    )}

                    {getSolutionFieldType(formData.title) === 'contact' && (
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                          Proposed Phone Number
                        </Typography>
                        <TextField
                          fullWidth
                          type="tel"
                          value={formData.proposedPhone || ''}
                          onChange={(e) => setFormData({ ...formData, proposedPhone: e.target.value, requiresApproval: true })}
                          placeholder="+1-555-123-4567"
                          variant="outlined"
                          helperText="Enter phone number with country code"
                        />
                      </Box>
                    )}

                    {getSolutionFieldType(formData.title) === 'labels' && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Proposed Labels/Tags
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleGenerateAISolution('labels')}
                            disabled={generatingAI}
                            startIcon={generatingAI && aiFieldType === 'labels' ? <CircularProgress size={16} /> : null}
                            sx={{
                              ml: 'auto',
                              borderColor: '#667eea',
                              color: '#667eea',
                              '&:hover': {
                                borderColor: '#764ba2',
                                backgroundColor: 'rgba(102, 126, 234, 0.04)'
                              }
                            }}
                          >
                            {generatingAI && aiFieldType === 'labels' ? 'Generating...' : '✨ Generate with AI'}
                          </Button>
                        </Box>
                        <Autocomplete
                          multiple
                          freeSolo
                          options={[]}
                          value={formData.proposedLabels || []}
                          onChange={(event, newValue) => {
                            setFormData({ 
                              ...formData, 
                              proposedLabels: newValue,
                              requiresApproval: true 
                            });
                          }}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                key={index}
                                label={option}
                                {...getTagProps({ index })}
                                sx={{ bgcolor: '#667eea', color: 'white', mb: 0.5 }}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Type labels and press Enter to add..."
                              helperText="Type custom labels for organizing locations"
                            />
                          )}
                        />
                      </Box>
                    )}

                    {getSolutionFieldType(formData.title) === 'attributes' && (
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                          Proposed Attributes
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Select attributes that describe your business features and policies
                        </Typography>
                        {Object.entries(GMB_ATTRIBUTES).map(([category, attrs]) => (
                          <Box key={category} sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#667eea' }}>
                              {category}
                            </Typography>
                            <Autocomplete
                              multiple
                              options={attrs}
                              value={(formData.proposedAttributes || []).filter(attr => attrs.includes(attr))}
                              onChange={(event, newValue) => {
                                // Merge with other category selections
                                const otherAttrs = (formData.proposedAttributes || []).filter(attr => !attrs.includes(attr));
                                setFormData({ 
                                  ...formData, 
                                  proposedAttributes: [...otherAttrs, ...newValue],
                                  requiresApproval: true 
                                });
                              }}
                              renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                  <Chip
                                    key={index}
                                    label={option}
                                    size="small"
                                    {...getTagProps({ index })}
                                    sx={{ bgcolor: '#667eea', color: 'white', mb: 0.5 }}
                                  />
                                ))
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder={`Select ${category.toLowerCase()}...`}
                                  size="small"
                                />
                              )}
                              sx={{ mb: 1 }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}

                    {getSolutionFieldType(formData.title) === 'qanda' && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Proposed Q&A
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleGenerateAISolution('qanda')}
                            disabled={generatingAI}
                            startIcon={generatingAI && aiFieldType === 'qanda' ? <CircularProgress size={16} /> : null}
                            sx={{
                              ml: 'auto',
                              borderColor: '#667eea',
                              color: '#667eea',
                              '&:hover': {
                                borderColor: '#764ba2',
                                backgroundColor: 'rgba(102, 126, 234, 0.04)'
                              }
                            }}
                          >
                            {generatingAI && aiFieldType === 'qanda' ? 'Generating...' : '✨ Generate with AI'}
                          </Button>
                        </Box>
                        {Array.isArray(formData.proposedQandA) && formData.proposedQandA.map((qa, index) => (
                          <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                            <TextField
                              fullWidth
                              label="Question"
                              value={qa.question || ''}
                              onChange={(e) => {
                                const newQandA = [...formData.proposedQandA];
                                newQandA[index] = { ...qa, question: e.target.value };
                                setFormData({ ...formData, proposedQandA: newQandA, requiresApproval: true });
                              }}
                              sx={{ mb: 1 }}
                            />
                            <TextField
                              fullWidth
                              label="Answer"
                              multiline
                              rows={2}
                              value={qa.answer || ''}
                              onChange={(e) => {
                                const newQandA = [...formData.proposedQandA];
                                newQandA[index] = { ...qa, answer: e.target.value };
                                setFormData({ ...formData, proposedQandA: newQandA, requiresApproval: true });
                              }}
                            />
                          </Box>
                        ))}
                        <Button
                          size="small"
                          onClick={() => setFormData({ 
                            ...formData, 
                            proposedQandA: [...(formData.proposedQandA || []), { question: '', answer: '' }],
                            requiresApproval: true 
                          })}
                        >
                          + Add Question
                        </Button>
                      </Box>
                    )}

                    {getSolutionFieldType(formData.title) === 'generic' && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            Proposed Solution
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleGenerateAISolution('generic')}
                            disabled={generatingAI}
                            startIcon={generatingAI && aiFieldType === 'generic' ? <CircularProgress size={16} /> : null}
                            sx={{
                              ml: 'auto',
                              borderColor: '#667eea',
                              color: '#667eea',
                              '&:hover': {
                                borderColor: '#764ba2',
                                backgroundColor: 'rgba(102, 126, 234, 0.04)'
                              }
                            }}
                          >
                            {generatingAI && aiFieldType === 'generic' ? 'Generating...' : '✨ Generate with AI'}
                          </Button>
                        </Box>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={formData.proposedSolution || ''}
                          onChange={(e) => setFormData({ ...formData, proposedSolution: e.target.value, requiresApproval: true })}
                          placeholder="Write the solution here or use AI to generate it... Client will approve/reject this."
                          variant="outlined"
                          helperText="Provide detailed instructions or solution for the client"
                        />
                      </Box>
                    )}

                    {formData.requiresApproval && (
                      <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          ⚠️ <strong>Approval Required:</strong> Client will need to approve this before it's applied to their GMB profile.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </>
            )}

            {/* File Upload Section - Show only for photo/post related tasks */}
            {requiresFileUpload(formData.title) && (
              <Grid item xs={12}>
                <Box sx={{ 
                  border: '2px dashed #0b91d6', 
                  borderRadius: '8px', 
                  padding: '20px', 
                  backgroundColor: '#f8f9fa',
                  textAlign: 'center'
                }}>
                  <input
                    accept="image/*,video/*,.pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    id="file-upload"
                    multiple
                    type="file"
                    onChange={handleFileUpload}
                  />
                  <label htmlFor="file-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<CloudUpload />}
                      sx={{ mb: 2 }}
                    >
                      Upload Photos/Files
                    </Button>
                  </label>
                  <Typography variant="body2" color="textSecondary">
                    Upload photos, videos, or documents to share with the client
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                    Supported: Images, Videos, PDF, Word documents
                  </Typography>

                  {/* File Preview */}
                  {filePreview.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 2, textAlign: 'left' }}>
                        Uploaded Files ({filePreview.length})
                      </Typography>
                      <Grid container spacing={2}>
                        {filePreview.map((file, index) => (
                          <Grid item xs={6} sm={4} md={3} key={index}>
                            <Card sx={{ position: 'relative' }}>
                              {file.type === 'image' ? (
                                <Box
                                  component="img"
                                  src={file.url}
                                  alt={file.name}
                                  sx={{
                                    width: '100%',
                                    height: '120px',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : (
                                <Box sx={{ 
                                  height: '120px', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  backgroundColor: '#e3f2fd'
                                }}>
                                  <AttachFile sx={{ fontSize: 48, color: '#0b91d6' }} />
                                </Box>
                              )}
                              <CardContent sx={{ p: 1 }}>
                                <Typography variant="caption" noWrap title={file.name}>
                                  {file.name}
                                </Typography>
                              </CardContent>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveFile(index)}
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,1)'
                                  }
                                }}
                              >
                                <Close fontSize="small" />
                              </IconButton>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControl fullWidth required error={!formData.assignedTo && submitting}>
                <InputLabel>Assign To GMB Profile</InputLabel>
                <Select
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleInputChange}
                  label="Assign To GMB Profile"
                >
                  {gmbProfiles.filter(cp => cp.profiles.length > 0).length === 0 ? (
                    <MenuItem disabled>No GMB profiles with permissions available</MenuItem>
                  ) : (
                    gmbProfiles
                      .filter(clientProfile => clientProfile.profiles.length > 0)
                      .map((clientProfile) => [
                        <ListSubheader key={`header-${clientProfile.clientId}`} sx={{ fontWeight: 700, color: '#0b91d6' }}>
                          {clientProfile.clientName}
                        </ListSubheader>,
                        clientProfile.profiles.map((profile) => (
                          <MenuItem 
                            key={`${clientProfile.clientId}-${profile.id}`} 
                            value={`${clientProfile.clientId}-${profile.id}`}
                            sx={{ pl: 4 }}
                          >
                            {profile.name}
                          </MenuItem>
                        ))
                      ])
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Priority</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  label="Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in-progress">In Progress</MenuItem>
                  {/* Completed and Cancelled only available when editing, not creating */}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Assign Date"
                name="assignDate"
                type="date"
                value={formData.assignDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
                helperText="Date when task will be assigned to GMB profile"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Assign Time"
                name="assignTime"
                type="time"
                value={formData.assignTime}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
                helperText="Time when task will be assigned"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                placeholder="e.g., Profile Management, Citations"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Estimated Hours"
                name="estimatedHours"
                type="number"
                value={formData.estimatedHours}
                onChange={handleInputChange}
                inputProps={{ min: 0, step: 0.5 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tags"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder="Comma separated tags"
                helperText="e.g., GMB, Profile, Urgent"
              />
            </Grid>
          </Grid>

          {/* Task List Section */}
          {!selectedTask && taskList.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>📋 Tasks Ready to Submit ({taskList.length})</span>
                  <Chip label={`${taskList.length} task${taskList.length > 1 ? 's' : ''}`} color="primary" size="small" />
                </Typography>
                <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {taskList.map((task, index) => (
                    <Card key={task.id} sx={{ mb: 1, position: 'relative' }}>
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {index + 1}. {task.title}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem', mb: 1 }}>
                              {task.description.substring(0, 80)}{task.description.length > 80 ? '...' : ''}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip label={task.priority} size="small" color={task.priority === 'high' ? 'error' : task.priority === 'medium' ? 'warning' : 'default'} />
                              {task.attachments.length > 0 && (
                                <Chip icon={<AttachFile />} label={`${task.attachments.length} file${task.attachments.length > 1 ? 's' : ''}`} size="small" variant="outlined" />
                              )}
                            </Box>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveFromList(task.id)}
                            sx={{ color: '#d32f2f' }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!selectedTask && (
              <>
                <Button
                  onClick={handleAddToList}
                  variant="outlined"
                  startIcon={<Add />}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#0b91d6',
                    color: '#0b91d6',
                    '&:hover': { borderColor: '#0a7bb8', backgroundColor: 'rgba(11, 145, 214, 0.1)' }
                  }}
                >
                  Add to List
                </Button>
                {taskList.length > 0 && (
                  <Button
                    onClick={handleSubmitAll}
                    variant="contained"
                    disabled={loading}
                    sx={{
                      backgroundColor: '#4caf50',
                      textTransform: 'none',
                      '&:hover': { backgroundColor: '#45a049' }
                    }}
                  >
                    {loading ? <CircularProgress size={20} /> : `Submit All (${taskList.length})`}
                  </Button>
                )}
              </>
            )}
            
            {/* Schedule Post Button - Only show for new tasks with photo/post/image category */}
            {!selectedTask && formData.assignedTo && formData.category && 
             (formData.category.toLowerCase().includes('photo') || 
              formData.category.toLowerCase().includes('post') || 
              formData.category.toLowerCase().includes('image')) && (
              <Button
                onClick={handleOpenSchedulePost}
                variant="outlined"
                startIcon={<CalendarMonth />}
                disabled={loading}
                sx={{
                  borderColor: '#ff9800',
                  color: '#ff9800',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { 
                    borderColor: '#f57c00', 
                    backgroundColor: '#fff3e0' 
                  }
                }}
              >
                📅 Schedule Post
              </Button>
            )}
            
            <Button
              onClick={selectedTask ? handleSubmit : handleSubmit}
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: '#0b91d6',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#0a7bb8' }
              }}
            >
              {selectedTask ? 'Update Task' : 'Create Single Task'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* View Task Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={700}>
              Task Details
            </Typography>
            <IconButton onClick={() => setViewDialog(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTask && (
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Title
              </Typography>
              <Typography variant="body1" fontWeight={600} mb={2}>
                {selectedTask.title}
              </Typography>

              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Description
              </Typography>
              <Typography variant="body1" mb={2}>
                {selectedTask.description}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Assigned To
                  </Typography>
                  <Typography variant="body1">{selectedTask.assignedTo}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Priority
                  </Typography>
                  <Chip
                    label={selectedTask.priority.toUpperCase()}
                    color={getPriorityColor(selectedTask.priority)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Status
                  </Typography>
                  <Chip
                    label={selectedTask.status.replace('-', ' ').toUpperCase()}
                    color={getStatusColor(selectedTask.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Due Date
                  </Typography>
                  <Typography variant="body1">{selectedTask.dueDate}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Category
                  </Typography>
                  <Typography variant="body1">{selectedTask.category}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Estimated Hours
                  </Typography>
                  <Typography variant="body1">{selectedTask.estimatedHours}h</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Tags
                  </Typography>
                  <Typography variant="body1">{selectedTask.tags}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setViewDialog(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Tasks Dialog */}
      <Dialog open={viewTasksDialog} onClose={() => setViewTasksDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Tasks for {selectedProfile?.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Client: {selectedClient?.name}
              </Typography>
            </Box>
            <IconButton onClick={() => setViewTasksDialog(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {profileTasks.length === 0 ? (
            <Typography color="textSecondary" align="center" py={4}>
              No tasks found for this profile
            </Typography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Title</StyledTableCell>
                  <StyledTableCell>Type</StyledTableCell>
                  <StyledTableCell>Priority</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Assign Date</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profileTasks.map((task) => (
                  <TableRow key={task.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{task.title}</Typography>
                      {task.description && (
                        <Typography variant="body2" color="textSecondary" noWrap>
                          {task.description.substring(0, 50)}...
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(() => {
                          const type = task.createdByType?.toLowerCase();
                          if (type === 'ai') return '🤖 AI';
                          if (type === 'system') return '⚙️ System';
                          return '👤 Admin';
                        })()}
                        color={(() => {
                          const type = task.createdByType?.toLowerCase();
                          if (type === 'ai') return 'default';
                          if (type === 'system') return 'secondary';
                          return 'primary';
                        })()}
                        size="small"
                        variant={(() => {
                          const type = task.createdByType?.toLowerCase();
                          if (type === 'ai' || type === 'system') return 'outlined';
                          return 'filled';
                        })()}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={task.priority?.toUpperCase()}
                        color={getPriorityColor(task.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={task.status?.replace('-', ' ').toUpperCase()}
                        color={getStatusColor(task.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {task.assignDate} {task.assignTime && `at ${task.assignTime}`}
                    </TableCell>
                    <TableCell>
                      <ActionButton 
                        onClick={() => handleToggleVisibility(task.id, task.isVisibleToClient)}
                        color={task.isVisibleToClient ? "success" : "default"}
                        title={task.isVisibleToClient ? "Hide from client" : "Show to client"}
                      >
                        {task.isVisibleToClient ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                      </ActionButton>
                      <ActionButton onClick={() => {
                        setSelectedTask(task);
                        setViewDialog(true);
                      }} color="primary">
                        <Visibility fontSize="small" />
                      </ActionButton>
                      <ActionButton onClick={() => {
                        setFormData({
                          title: task.title,
                          description: task.description,
                          assignedTo: `${selectedClient.id}-${selectedProfile.id}`,
                          priority: task.priority,
                          status: task.status,
                          assignDate: task.assignDate,
                          assignTime: task.assignTime,
                          category: task.category,
                          estimatedHours: task.estimatedHours,
                          tags: task.tags
                        });
                        setSelectedTask(task);
                        setViewTasksDialog(false);
                        setOpenDialog(true);
                      }} color="primary">
                        <Edit fontSize="small" />
                      </ActionButton>
                      <ActionButton onClick={() => {
                        if (window.confirm('Are you sure you want to delete this task?')) {
                          handleDeleteTask(task.id);
                          setViewTasksDialog(false);
                        }
                      }} color="error">
                        <Delete fontSize="small" />
                      </ActionButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setViewTasksDialog(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Assign Task Dialog */}
      <Dialog 
        open={bulkAssignDialog} 
        onClose={() => setBulkAssignDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6">Bulk Assign Task to Multiple Profiles</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<LibraryBooks />}
            onClick={() => setTaskCatalogModalOpen(true)}
            sx={{
              color: 'white',
              borderColor: 'white',
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Browse Catalog
          </Button>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Assignment Type Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                  Assign To:
                </Typography>
                <Select
                  value={bulkAssignType}
                  onChange={(e) => setBulkAssignType(e.target.value)}
                >
                  <MenuItem value="all">All Users & Profiles</MenuItem>
                  <MenuItem value="selected_clients">Selected Clients (All their profiles)</MenuItem>
                  <MenuItem value="selected_profiles">Selected Specific Profiles</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Show client selector if selected_clients */}
            {bulkAssignType === 'selected_clients' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Select Clients:
                  </Typography>
                  <Select
                    multiple
                    value={selectedClientsForBulk}
                    onChange={(e) => setSelectedClientsForBulk(e.target.value)}
                    renderValue={(selected) => 
                      selected.map(id => 
                        gmbProfiles.find(c => c.clientId === id)?.clientName
                      ).join(', ')
                    }
                  >
                    {gmbProfiles
                      .filter(client => client.profiles.length > 0)
                      .map((client) => (
                        <MenuItem key={client.clientId} value={client.clientId}>
                          <Checkbox checked={selectedClientsForBulk.indexOf(client.clientId) > -1} />
                          <ListItemText primary={`${client.clientName} (${client.profiles.length} profiles)`} />
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Show profile selector if selected_profiles */}
            {bulkAssignType === 'selected_profiles' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Select Profiles:
                  </Typography>
                  <Select
                    multiple
                    value={selectedProfilesForBulk}
                    onChange={(e) => setSelectedProfilesForBulk(e.target.value)}
                    renderValue={(selected) => `${selected.length} profiles selected`}
                  >
                    {gmbProfiles
                      .filter(client => client.profiles.length > 0)
                      .map((client) => [
                        <ListSubheader key={`header-${client.clientId}`}>
                          {client.clientName}
                        </ListSubheader>,
                        ...client.profiles.map((profile) => (
                          <MenuItem 
                            key={profile.id} 
                            value={{ 
                              clientId: client.clientId, 
                              profileId: profile.id, 
                              profileName: profile.name 
                            }}
                          >
                            <Checkbox 
                              checked={selectedProfilesForBulk.some(p => p.profileId === profile.id)} 
                            />
                            <ListItemText primary={profile.name} />
                          </MenuItem>
                        ))
                      ])}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Task Details */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select Predefined Task (Optional)</InputLabel>
                <Select
                  value=""
                  onChange={(e) => {
                    const selectedTitle = e.target.value;
                    if (selectedTitle && PREDEFINED_TASKS[selectedTitle]) {
                      setFormData({
                        ...formData,
                        title: selectedTitle,
                        description: PREDEFINED_TASKS[selectedTitle]
                      });
                    }
                  }}
                  label="Select Predefined Task (Optional)"
                >
                  <MenuItem value="">
                    <em>-- Select a predefined task or enter custom --</em>
                  </MenuItem>
                  {Object.keys(PREDEFINED_TASKS).map((taskTitle) => (
                    <MenuItem key={taskTitle} value={taskTitle}>
                      {taskTitle}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Task Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                helperText="Select from predefined tasks above or enter custom title"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                helperText="Auto-filled when you select a predefined task"
              />
            </Grid>

            {/* Show file upload if task is related to photos/posts */}
            {(formData.title.toLowerCase().includes('photo') || 
              formData.title.toLowerCase().includes('image') || 
              formData.title.toLowerCase().includes('logo') || 
              formData.title.toLowerCase().includes('cover') ||
              formData.title.toLowerCase().includes('upload') ||
              formData.title.toLowerCase().includes('premises') ||
              formData.title.toLowerCase().includes('product') ||
              formData.title.toLowerCase().includes('interior') ||
              formData.title.toLowerCase().includes('exterior')) && (
              <Grid item xs={12}>
                <Box sx={{ 
                  border: '2px dashed #0b91d6', 
                  borderRadius: '8px', 
                  p: 2,
                  backgroundColor: '#f8f9fa'
                }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600} color="#0b91d6">
                    Upload Photos/Images
                  </Typography>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="bulk-file-upload"
                    multiple
                    type="file"
                    onChange={handleFileUpload}
                  />
                  <label htmlFor="bulk-file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUpload />}
                      sx={{ 
                        textTransform: 'none',
                        borderColor: '#0b91d6',
                        color: '#0b91d6',
                        '&:hover': {
                          borderColor: '#0a7bb8',
                          backgroundColor: '#e3f2fd'
                        }
                      }}
                    >
                      Choose Files
                    </Button>
                  </label>
                  {uploadedFiles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {uploadedFiles.length} file(s) selected:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {filePreview.map((preview, index) => (
                          <Box
                            key={index}
                            sx={{
                              position: 'relative',
                              width: 80,
                              height: 80,
                              borderRadius: '8px',
                              overflow: 'hidden',
                              border: '1px solid #e0e0e0'
                            }}
                          >
                            <img
                              src={preview}
                              alt={`Preview ${index + 1}`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveFile(index)}
                              sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 1)'
                                }
                              }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in-progress">In Progress</MenuItem>
                  {/* Completed only available when editing, not creating */}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Estimated Hours"
                type="number"
                value={formData.estimatedHours}
                onChange={(e) => setFormData({ ...formData, estimatedHours: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Assign Date"
                type="date"
                value={formData.assignDate}
                onChange={(e) => setFormData({ ...formData, assignDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Assign Time"
                type="time"
                value={formData.assignTime}
                onChange={(e) => setFormData({ ...formData, assignTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBulkAssignDialog(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkAssignTask}
            variant="contained"
            disabled={loading || !formData.title}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              textTransform: 'none'
            }}
          >
            {loading ? 'Assigning...' : 'Bulk Assign Task'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submission Progress Backdrop */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 2000,
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }}
        open={submitting}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            minWidth: '400px',
            maxWidth: '500px',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
          }}
        >
          <CircularProgress
            size={80}
            thickness={4}
            sx={{ color: '#0b91d6', mb: 3 }}
          />
          <Typography variant="h6" sx={{ color: '#2c3e50', mb: 2, fontWeight: 600 }}>
            Submitting Tasks...
          </Typography>
          <Typography variant="body1" sx={{ color: '#7f8c8d', mb: 3 }}>
            {submitStatus}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={submitProgress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#4caf50',
                borderRadius: 4
              }
            }}
          />
          <Typography variant="body2" sx={{ color: '#95a5a6', mt: 2 }}>
            {Math.round(submitProgress)}% Complete
          </Typography>
        </Box>
      </Backdrop>

      {/* Schedule Post Modal */}
      <SchedulePostModal
        open={schedulePostModalOpen}
        onClose={handleCloseSchedulePostModal}
        onSuccess={handleSchedulePostSuccess}
        taskCreationMode={true}
        preloadedImage={capturedImageForSchedule}
        initialContent={schedulePostData ? `${schedulePostData.taskTitle}\n\n${schedulePostData.taskDescription || ''}` : ''}
        accountId={schedulePostData ? schedulePostData.clientId : null}
        locationId={schedulePostData ? schedulePostData.locationId : null}
        businessCategory=""
      />

      {/* Task Catalog Modal */}
      <TaskCatalogModal
        open={taskCatalogModalOpen}
        onClose={() => setTaskCatalogModalOpen(false)}
        onSelectTask={handleSelectTaskFromCatalog}
      />
    </PageContainer>
  );
};

export default Tasks;
